import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext.tsx';
import saccoGoalsService from "@saccoGoalsService";

const QUERY_KEY = 'sacco_contribution_goals';

export function useFetchSaccoGoals(membershipId: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [QUERY_KEY, membershipId],
    queryFn: async () => {
      if (!user) {
        throw Error('User is not authenticated');
      }
      return await saccoGoalsService.fetchGoals(user.id, membershipId);
    },
    enabled: !!user && !!membershipId,
  });
}

/**
 * Custom hook for creating a Sacco contribution goal.
 *
 * @param {string} membershipId - The unique identifier of the Sacco membership for which the goal is being created.
 * @return {object} A mutation object from the `useMutation` hook, which includes methods and properties
 *                  for triggering the mutation and handling its state (e.g., loading, error, success).
 */
export function useCreateSaccoGoal(membershipId: string) {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: SaccoContributionGoalRequest) => {
      if (!user) {
        throw Error('User is not authenticated');
      }
      return await saccoGoalsService.createContributionGoal(user.id, input);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY, membershipId] }),
  });
}

/**
 * A custom hook that provides functionality to update a Sacco contribution goal.
 *
 * @param {string} membershipId - The unique identifier of the membership associated with the Sacco contribution goal to be updated.
 * @return {object} A mutation object from the `useMutation` hook, which includes properties and functions for managing the mutation state and performing the update operation.
 */
export function useUpdateSaccoGoal(membershipId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SaccoContributionGoalUpdateRequest) => {
      return await saccoGoalsService.updateContributionGoal(input);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY, membershipId] }),
  });
}

/**
 * Custom hook to delete a Sacco contribution goal.
 *
 * @param {string} membershipId - The ID of the membership associated with the Sacco contribution goal.
 * @return {object} A mutation object from react-query's `useMutation` hook, allowing you to trigger the deletion and access its state and lifecycle callbacks.
 */
export function useDeleteSaccoGoal(membershipId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await saccoGoalsService.deleteContributionGoal(id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: [QUERY_KEY, membershipId] }),
  });
}
